import type { TechnologyModuleGuide } from './TechnologyModuleGuide';

export const TECHNOLOGY_MODULE_GUIDES: Record<string, TechnologyModuleGuide> = {
  'technology.overview': {
    title: 'Visão geral',
    description:
      'Painel consolidado, mapa de implantação e distribuição das aplicações na infraestrutura.',
    objective:
      'Ver o volume do módulo, onde cada aplicação da composição está implantada e como as aplicações se distribuem nos servidores, usando a capacidade do cadastro enquanto não houver telemetria.',
    covers: [
      'Números consolidados do módulo',
      'Mapa da infraestrutura por sistema e ambiente',
      'Distribuição de Aplicações por servidor e por aplicação',
      'Capacidade cadastrada (vCPU, memória e disco) e utilização só quando houver medição',
      'Configuração da hospedagem no detalhe da aplicação',
    ],
    relatesTo: [
      'Sistemas — quais aplicações formam o produto',
      'Hospedagem — aplicação × ambiente → servidor',
      'Inventário — linguagens vinculadas à aplicação',
    ],
    outcomes: ['Leitura rápida do parque tecnológico e da implantação de cada produto'],
  },
  'technology.applications': {
    title: 'Aplicações',
    description: 'Cadastro das aplicações, repositórios, pipelines e versões.',
    objective:
      'Centralizar o inventário dos sistemas e produtos da organização, para que cada aplicação conheça sua hospedagem, integrações, repositórios e responsáveis.',
    covers: [
      'Nome e versão',
      'Repositório e pipeline',
      'Hospedagem (ambiente, infraestrutura e endereço)',
      'Owners e times responsáveis',
    ],
    relatesTo: [
      'Inventário — tecnologias padrão usadas pela aplicação (ex.: PostgreSQL, .NET)',
      'Hospedagem — ambiente, infraestrutura e endereço de cada implantação',
      'Sistemas — várias aplicações formam o produto',
    ],
    outcomes: [
      'Visão única do portfólio de software',
      'Base para rastreabilidade de mudanças e auditorias',
    ],
  },
  'technology.infrastructure': {
    title: 'Infraestrutura',
    description: 'Inventário de servidores, cloud, bancos, storage, CDN e balanceadores.',
    objective:
      'Cadastrar as instâncias concretas (servidor, banco, cache, storage) onde as aplicações rodam.',
    covers: [
      'VPS, servidores e cloud',
      'Bancos de dados, storage e CDN',
      'Load balancers e recursos de rede',
      'Fornecedor, IP, SO, CPU, RAM, disco, região, moeda e custo mensal',
    ],
    relatesTo: [
      'Aplicações — hospedagem por ambiente neste ativo',
      'Certificados — quando o certificado está no ativo',
    ],
    outcomes: [
      'Inventário confiável para operação e auditoria',
      'Base para gestão de capacidade e risco',
    ],
  },
  'technology.integrations': {
    title: 'Integrações',
    description: 'Conectores externos ligados à aplicação, ao cofre e às chaves.',
    objective:
      'Catalogar cada integração com fornecedor, autenticação, endpoint, limite, SLA e a aplicação que a usa.',
    covers: [
      'Fornecedor e tipo',
      'Autenticação, endpoint e escopo',
      'Limite, SLA e status',
      'Credencial do cofre e chave do inventário',
    ],
    relatesTo: [
      'Aplicações — sistema que consome o conector',
      'Cofre — credencial usada na autenticação',
      'Chaves — identidade de acesso, quando houver',
    ],
    outcomes: [
      'Mapa dos conectores da aplicação',
      'Rastro da credencial usada em cada integração',
    ],
  },
  'technology.secrets': {
    title: 'Cofre de Credenciais',
    description: 'Senhas, tokens, API keys e demais segredos com auditoria e rotação.',
    objective:
      'Guardar segredos com criptografia, versionamento, expiração, compartilhamento seguro e auditoria de acesso — sabendo quais aplicações e ambientes os utilizam.',
    covers: [
      'Senhas, tokens, API keys, OAuth e JWT',
      'Licenças sensíveis e demais credenciais',
      'Versionamento, expiração e rotação',
      'Histórico de alterações e auditoria de acesso',
    ],
    relatesTo: [
      'Aplicações e ambientes — quem consome o segredo',
      'Integrações — autenticação dos conectores',
      'Certificados e chaves — identidade e acesso',
    ],
    outcomes: [
      'Controle de acesso a informações sensíveis',
      'Evidências para ISO 27001 e auditorias de segurança',
    ],
  },
  'technology.certificates': {
    title: 'Certificados',
    description: 'SSL, A1, A3 e certificados de API com vencimento calculado.',
    objective:
      'Controlar o certificado de cada aplicação: tipo, emissor, nome, expiração e, quando houver, ambiente, ativo e credencial no cofre.',
    covers: [
      'SSL/TLS, A1, A3 e certificado de API',
      'Emissor, nome e data de expiração',
      'Aplicação, ambiente e ativo de infraestrutura',
      'Situação calculada: válido, vence em 30 dias, vencido ou revogado',
    ],
    relatesTo: [
      'Aplicações e ambientes — onde o certificado vale',
      'Infraestrutura — ativo em que ele está aplicado',
      'Cofre — material sensível, quando guardado lá',
    ],
    outcomes: [
      'Lista ordenada pelo vencimento',
      'Sinal de certificado vencido ou próximo do vencimento',
    ],
  },
  'technology.keys': {
    title: 'Chaves SSH/API',
    description: 'Chaves de acesso e identidade para infraestrutura e integrações.',
    objective:
      'Inventariar chaves SSH e de API usadas para acesso a servidores, repositórios e serviços, com dono, escopo e rotação.',
    covers: [
      'Chaves SSH e pares públicos/privados (metadados)',
      'Chaves de API e identidades de máquina',
      'Escopo de uso e responsável',
      'Política de rotação e revogação',
    ],
    relatesTo: [
      'Infraestrutura — servidores e acessos',
      'Integrações — autenticação por chave',
      'Cofre — armazenamento do material secreto',
    ],
    outcomes: [
      'Menor risco de chaves órfãs ou compartilhadas',
      'Base para revogação rápida em incidentes',
    ],
  },
  'technology.ai-models': {
    title: 'Modelos de IA',
    description: 'Usos de IA na prestação do serviço, com classificação de risco.',
    objective:
      'Registrar cada funcionalidade de IA ligada à aplicação que presta o serviço e classificá-la pela matriz de dados, autonomia, impacto aos titulares e criticidade do processo.',
    covers: [
      'Funcionalidade, aplicação e modelo do inventário',
      'Produto já vinculado à aplicação',
      'Dados, autonomia, impacto aos titulares e criticidade do processo',
      'Classe calculada, justificativa e próxima revisão',
    ],
    relatesTo: [
      'Aplicações — sistema que presta o serviço',
      'Inventário tecnológico — modelos da categoria Modelo de IA',
      'Produtos — serviço comercial já ligado à aplicação',
    ],
    outcomes: [
      'Inventário de usos de IA com classe de risco',
      'Evidência da matriz para governança e auditoria',
    ],
  },
  'technology.ai-prompts': {
    title: 'Prompts',
    description: 'Prompts versionados, ligados ao uso de IA e ao modelo do inventário.',
    objective:
      'Guardar o texto e a temperatura de cada prompt, com modelo, fallback e a versão que sobe quando o conteúdo muda.',
    covers: [
      'Texto do prompt e temperatura',
      'Uso de IA, modelo e fallback',
      'Versão incrementada a cada mudança de conteúdo',
      'Notas de dataset e responsável',
    ],
    relatesTo: [
      'Modelos — uso de IA já classificado',
      'Inventário tecnológico — modelos da categoria Modelo de IA',
      'Benchmarks — avaliação do prompt',
    ],
    outcomes: [
      'Controle de mudança do comportamento de IA',
      'Rastro de versão e responsável',
    ],
  },
  'technology.ai-benchmarks': {
    title: 'Benchmarks',
    description: 'Avaliações de qualidade, latência e custo de um uso de IA.',
    objective:
      'Registrar o resultado de cada avaliação: cenário, dataset, qualidade, latência, custo estimado e recomendação.',
    covers: [
      'Cenário ligado ao uso e, se houver, ao prompt',
      'Modelo do inventário avaliado',
      'Qualidade, latência e custo estimado',
      'Resultado e recomendação',
    ],
    relatesTo: [
      'Modelos — uso avaliado',
      'Prompts — texto que participou da avaliação',
      'Custos — gasto operacional do mesmo uso',
    ],
    outcomes: [
      'Evidência para manter ou trocar um modelo',
      'Histórico de qualidade do uso de IA',
    ],
  },
  'technology.ai-costs': {
    title: 'Custos de IA',
    description: 'Custo mensal de cada uso de IA, com volume e orçamento.',
    objective:
      'Lançar o gasto de cada uso por mês, com tokens, valor, moeda e orçamento. O mês que ultrapassa o orçamento fica marcado na lista.',
    covers: [
      'Um lançamento por uso e mês',
      'Valor, moeda e volume de tokens',
      'Orçamento opcional',
      'Sinal de valor acima do orçamento',
    ],
    relatesTo: [
      'Modelos — uso que gerou o custo',
      'Benchmarks — custo estimado da avaliação',
    ],
    outcomes: [
      'Visão do gasto operacional de IA',
      'Alerta simples quando o mês passa do orçamento',
    ],
  },
  'technology.documentation': {
    title: 'Documentação',
    description: 'Wikis e manuais de produto com páginas, tags e links públicos.',
    objective:
      'Publicar documentação viva — manuais de produto, runbooks e guias — com árvore de páginas, passadores e compartilhamento externo.',
    covers: [
      'Manuais e espaços de documentação',
      'Páginas com rich text, imagens e vídeos embutidos',
      'Tags, status (rascunho/publicado/arquivado) e visibilidade',
      'Links públicos para clientes',
    ],
    relatesTo: [
      'Produtos — manuais vinculados ao catálogo comercial',
      'Aplicações — documentação técnica dos ativos',
      'Sucesso do Cliente — guias compartilháveis',
    ],
    outcomes: [
      'Conhecimento acessível internamente e via link público',
      'Navegação guiada com passadores entre páginas',
    ],
  },
  'technology.deployments': {
    title: 'Deploys',
    description:
      'Registro de releases, aprovações, ambientes afetados, rollbacks e a documentação permanente de cada release.',
    objective:
      'Registrar deploys realizados (sem substituir o Azure DevOps): versão, quem aprovou, ambientes afetados, tempo, resultado e rollback. Integração Azure importa WI/PR e atualiza status. Geração de release notes por IA (commits → prompt → salvar na release) fica para depois.',
    covers: [
      'Versão liberada e mudanças entregues',
      'Ambiente da implantação (desenvolvimento, homologação, produção ou disaster recovery)',
      'Aprovadores e horário',
      'Resultado, tempo e rollback',
      'Releases — o que entrou em cada versão',
    ],
    relatesTo: [
      'Aplicações — produto versionado',
      'Hospedagem — destino da implantação',
      'Documentação — notas e procedimentos de mudança',
    ],
    outcomes: [
      'Histórico auditável de mudanças em produção',
      'Rastreabilidade para incidentes e qualidade',
    ],
  },
  'technology.catalog': {
    title: 'Inventário Tecnológico',
    description: 'Catálogo único das tecnologias utilizadas pela organização.',
    objective:
      'Manter um catálogo único do que a empresa utiliza (ex.: PostgreSQL 17), com responsável, projetos, fornecedor e licença.',
    covers: [
      'Tecnologia, versão e categoria',
      'Fornecedor e tipo de licença',
      'Responsável técnico',
      'Projetos e aplicações que utilizam',
    ],
    relatesTo: [
      'Aplicações — stack tecnológica de cada aplicação (API, front, jobs, banco…)',
      'Infraestrutura — instância concreta onde a aplicação roda',
      'Documentação — padrões oficiais',
    ],
    outcomes: [
      'Padronização tecnológica da organização',
      'Visibilidade de risco e obsolescência',
    ],
  },
  'technology.backups': {
    title: 'Backups',
    description: 'Políticas, periodicidade, último backup e testes de restore.',
    objective:
      'Cadastrar políticas de backup e restore, com periodicidade, local, último backup e evidência de teste de restauração.',
    covers: [
      'Política e periodicidade',
      'Local de armazenamento',
      'Último backup e status',
      'Testes de restore e responsáveis',
    ],
    relatesTo: [
      'Infraestrutura e bancos — ativos protegidos',
      'Hospedagem — escopo de proteção',
      'Documentação — procedimentos de recuperação',
    ],
    outcomes: [
      'Continuidade de negócio mensurável',
      'Evidências de recuperação para auditoria',
    ],
  },
  'technology.monitoring': {
    title: 'Monitoramento',
    description: 'Observabilidade dos ativos tecnológicos, incidentes e integrações.',
    objective:
      'Registrar e correlacionar saúde dos ativos (CPU, RAM, disco, latência, erros e incidentes), com possibilidade de integração a Grafana, Prometheus e UptimeRobot.',
    covers: [
      'Métricas de infraestrutura e aplicação',
      'Latência, erros e disponibilidade',
      'Incidentes e post-mortems',
      'Integrações com ferramentas de observabilidade',
    ],
    relatesTo: [
      'Infraestrutura e aplicações — o que é observado',
      'Hospedagem — escopo do monitoramento',
      'Deploys — correlação com mudanças recentes',
    ],
    outcomes: [
      'Detecção precoce de problemas',
      'Contexto operacional para resposta a incidentes',
    ],
  },
};
